// CR207: Debug panel for canonical field visibility on public site
// Shows which canonical fields are present vs missing per deal

import { useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle, XCircle, AlertCircle, Info } from "lucide-react";
import { type Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

interface DealCanonicalDebugPanelProps {
  deals: Deal[];
  className?: string;
}

interface FieldCheck {
  name: string;
  present: boolean;
  value: unknown;
  category: "value" | "dates" | "provenance" | "terms" | "meta";
}

function checkDealFields(deal: Deal): FieldCheck[] {
  return [
    // Value fields
    { name: "bonus_value_cad", present: deal.bonus_value_cad != null, value: deal.bonus_value_cad, category: "value" },
    { name: "qualifying_spend_cad", present: deal.qualifying_spend_cad != null, value: deal.qualifying_spend_cad, category: "value" },
    { name: "computed_value_cad", present: deal.computed_value_cad != null, value: deal.computed_value_cad, category: "value" },
    { name: "effective_percent_back", present: deal.effective_percent_back != null, value: deal.effective_percent_back, category: "value" },
    { name: "discount_percent", present: deal.discount_percent != null, value: deal.discount_percent, category: "value" },
    { name: "points_amount", present: deal.points_amount != null, value: deal.points_amount, category: "value" },
    // Date fields
    { name: "start_date", present: !!deal.start_date, value: deal.start_date, category: "dates" },
    { name: "end_date", present: !!deal.end_date, value: deal.end_date, category: "dates" },
    // Provenance fields
    { name: "source_type", present: !!deal.source_type, value: deal.source_type, category: "provenance" },
    { name: "claim_url", present: !!deal.claim_url, value: deal.claim_url, category: "provenance" },
    { name: "deal_url", present: !!deal.deal_url, value: deal.deal_url, category: "provenance" },
    { name: "flyer_page_number", present: deal.flyer_page_number != null, value: deal.flyer_page_number, category: "provenance" },
    // Terms fields
    { name: "fine_print", present: !!deal.fine_print, value: deal.fine_print ? `${String(deal.fine_print).slice(0, 50)}...` : null, category: "terms" },
    { name: "exclusions", present: !!deal.exclusions, value: deal.exclusions, category: "terms" },
    // Meta fields
    { name: "eligibility_scope", present: !!deal.eligibility_scope, value: deal.eligibility_scope, category: "meta" },
    { name: "availability_scope", present: !!deal.availability_scope, value: deal.availability_scope, category: "meta" },
  ];
}

function DealFieldSummary({ deal, index }: { deal: Deal; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const fields = checkDealFields(deal);
  const presentCount = fields.filter(f => f.present).length;
  const totalCount = fields.length;
  const percentage = Math.round((presentCount / totalCount) * 100);
  
  const byCategory = {
    value: fields.filter(f => f.category === "value"),
    dates: fields.filter(f => f.category === "dates"),
    provenance: fields.filter(f => f.category === "provenance"),
    terms: fields.filter(f => f.category === "terms"),
    meta: fields.filter(f => f.category === "meta"),
  };
  
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-2 bg-muted/50 hover:bg-muted transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
          <span className="font-medium text-sm truncate max-w-[200px]">{deal.merchant}</span>
          <span className={cn(
            "text-xs px-1.5 py-0.5 rounded",
            percentage >= 80 ? "bg-green-100 text-green-700" :
            percentage >= 50 ? "bg-yellow-100 text-yellow-700" :
            "bg-red-100 text-red-700"
          )}>
            {presentCount}/{totalCount} ({percentage}%)
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-3 text-xs bg-background">
          {Object.entries(byCategory).map(([category, categoryFields]) => (
            <div key={category}>
              <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                {category}
              </div>
              <div className="grid grid-cols-2 gap-1">
                {categoryFields.map(field => (
                  <div key={field.name} className="flex items-center gap-1">
                    {field.present ? (
                      <CheckCircle className="w-3 h-3 text-green-500" />
                    ) : (
                      <XCircle className="w-3 h-3 text-red-400" />
                    )}
                    <span className={cn(
                      "truncate",
                      field.present ? "text-foreground" : "text-muted-foreground"
                    )}>
                      {field.name}
                      {field.present && field.value != null && (
                        <span className="text-muted-foreground ml-1">
                          = {typeof field.value === "number" ? field.value : String(field.value).slice(0, 20)}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DealCanonicalDebugPanel({ deals, className }: DealCanonicalDebugPanelProps) {
  const [expanded, setExpanded] = useState(false);
  
  if (deals.length === 0) return null;
  
  // Aggregate stats
  const allFields = deals.map(d => checkDealFields(d));
  const fieldNames = allFields[0]?.map(f => f.name) || [];
  
  const fieldStats = fieldNames.map(name => {
    const presentCount = allFields.filter(fields => 
      fields.find(f => f.name === name)?.present
    ).length;
    return { name, presentCount, percentage: Math.round((presentCount / deals.length) * 100) };
  });
  
  const overallPresent = allFields.reduce((sum, fields) => 
    sum + fields.filter(f => f.present).length, 0
  );
  const overallTotal = allFields.reduce((sum, fields) => sum + fields.length, 0);
  const overallPercentage = Math.round((overallPresent / overallTotal) * 100);
  
  // Find fields that are commonly missing (< 50% populated)
  const missingFields = fieldStats.filter(f => f.percentage < 50);
  
  return (
    <div className={cn("bg-card border border-border rounded-lg shadow-sm", className)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">CR207 Canonical Field Diagnostics</span>
          <span className={cn(
            "text-xs px-2 py-0.5 rounded-full",
            overallPercentage >= 80 ? "bg-green-100 text-green-700" :
            overallPercentage >= 50 ? "bg-yellow-100 text-yellow-700" :
            "bg-red-100 text-red-700"
          )}>
            {overallPercentage}% populated
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 border-t border-border space-y-4">
          {/* Overall stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-muted/50 rounded p-2">
              <div className="text-muted-foreground">Total Deals</div>
              <div className="font-semibold text-lg">{deals.length}</div>
            </div>
            <div className="bg-muted/50 rounded p-2">
              <div className="text-muted-foreground">Avg Fields Present</div>
              <div className="font-semibold text-lg">{Math.round(overallPresent / deals.length)}/{fieldNames.length}</div>
            </div>
            <div className="bg-muted/50 rounded p-2">
              <div className="text-muted-foreground">With Values</div>
              <div className="font-semibold text-lg">
                {deals.filter(d => d.bonus_value_cad != null || d.computed_value_cad != null).length}
              </div>
            </div>
            <div className="bg-muted/50 rounded p-2">
              <div className="text-muted-foreground">With Fine Print</div>
              <div className="font-semibold text-lg">
                {deals.filter(d => d.fine_print).length}
              </div>
            </div>
          </div>
          
          {/* Commonly missing fields */}
          {missingFields.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
              <div className="flex items-center gap-1 text-yellow-700 text-xs font-medium mb-1">
                <AlertCircle className="w-3 h-3" />
                Fields commonly missing ({`<`}50% populated)
              </div>
              <div className="flex flex-wrap gap-1">
                {missingFields.map(f => (
                  <span key={f.name} className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                    {f.name} ({f.percentage}%)
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Per-deal breakdown (first 5) */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              First {Math.min(5, deals.length)} Deals
            </div>
            {deals.slice(0, 5).map((deal, i) => (
              <DealFieldSummary key={i} deal={deal} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
